// /accounts/.../tenants/$tenantSlug/ha-instances — table-style panel.
// No detail page because the backend has no GET-by-id; selection
// affordances would have to read .find against the list and the v1
// data doesn't warrant it. ID is shown inline with a CopyButton.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { haInstances } from "../../api/endpoints/haInstances";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import { CopyButton } from "../../components/ui/CopyButton";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Modal } from "../../components/ui/Modal";
import { Table } from "../../components/ui/Table";
import { PageHeader } from "../../components/shell/PageHeader";
import type { HaInstanceOut } from "../../api/types/haInstance";

import { HaInstanceCreateForm } from "./HaInstanceCreateForm";

interface HaInstanceListPanelProps {
  accountSlug: string;
  tenantSlug: string;
}

export function HaInstanceListPanel({
  accountSlug,
  tenantSlug,
}: HaInstanceListPanelProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const q = useQuery({
    ...adminQueryOptions,
    queryKey: qk.haInstances(accountSlug, tenantSlug),
    queryFn: () =>
      haInstances.list(accountSlug, tenantSlug).then((r) => r.data),
  });

  return (
    <>
      <PageHeader
        title={`HA Instances · ${tenantSlug}`}
        subtitle="Backend exposes list + create only; no detail page."
        actions={
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            style={primaryBtn}
          >
            Create HA instance
          </button>
        }
      />

      {q.isPending && <p style={{ color: "var(--text-dim)" }}>Loading…</p>}
      {q.error && (
        <ErrorBanner error={q.error} title="Failed to list HA instances" />
      )}
      {q.data && (
        <Table<HaInstanceOut>
          columns={[
            {
              key: "base_url",
              header: "Base URL",
              render: (h) => (
                <a
                  href={h.base_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--accent)" }}
                >
                  {h.base_url}
                </a>
              ),
            },
            {
              key: "id",
              header: "ID",
              render: (h) => (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <code style={{ fontSize: "0.78rem", color: "var(--text-dim)" }}>
                    {h.id}
                  </code>
                  <CopyButton value={h.id} label="Copy" />
                </span>
              ),
            },
            {
              key: "llat_secret_id",
              header: "LLAT secret id",
              render: (h) =>
                h.llat_secret_id === null ? (
                  <span style={{ fontSize: "0.85rem", color: "var(--text-dim)", fontStyle: "italic" }}>
                    Not linked
                  </span>
                ) : (
                  <code style={{ fontSize: "0.78rem", color: "var(--text-dim)" }}>
                    {h.llat_secret_id}
                  </code>
                ),
            },
          ]}
          rows={q.data}
          rowKey={(h) => h.id}
          empty={
            <EmptyState
              title="No HA instances on this tenant"
              description="Create one to bind a Home Assistant deployment. Needs an existing llat tenant_secret first."
            />
          }
        />
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        closeDisabled={creating}
        title={`Create HA instance for ${tenantSlug}`}
        width={520}
      >
        <HaInstanceCreateForm
          accountSlug={accountSlug}
          tenantSlug={tenantSlug}
          onDone={() => setCreateOpen(false)}
          onPendingChange={setCreating}
        />
      </Modal>
    </>
  );
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--bg)",
  border: "none",
  padding: "8px 16px",
  borderRadius: 4,
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
};
